/**
 * Track 4 (retention and social layer): notifications, seasons, alliances, galactic events.
 * Pure data and schemas shared by the API and the web client; UI text is Hungarian.
 */
import { z } from 'zod';

// ---- Notifications ----

export const NOTIFICATION_KINDS = [
  'fleet_arrived', 'under_attack', 'build_done', 'research_done', 'treaty_offer', 'event_started', 'alliance',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NOTIFICATION_KIND_LABELS: Record<NotificationKind, string> = {
  fleet_arrived: 'Flotta megérkezett',
  under_attack: 'Támadás alatt',
  build_done: 'Építés / gyártás kész',
  research_done: 'Kutatás kész',
  treaty_offer: 'Szerződés-ajánlat',
  event_started: 'Galaktikus esemény',
  alliance: 'Szövetségi hírek',
};

export const NOTIFICATION_CHANNELS = ['push', 'discord'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** Delivery limits (per recipient, sliding window); the in-app inbox is never limited. */
export const NOTIFICATION_RATE_LIMITS: Record<NotificationChannel, { max: number; windowMs: number }> = {
  push: { max: 8, windowMs: 10 * 60_000 },
  discord: { max: 20, windowMs: 60_000 },
};

export interface NotificationView {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationSettingsView {
  pushEnabled: boolean;
  /** Public VAPID key, or null when the server has no push configured. */
  vapidPublicKey: string | null;
  pushSubscriptions: number;
  discordEnabled: boolean;
  /** The webhook URL itself is a secret and is never sent back; this only says whether one is set. */
  discordConfigured: boolean;
  kinds: Record<NotificationKind, { push: boolean; discord: boolean }>;
}

const KindSetting = z.object({ push: z.boolean(), discord: z.boolean() }).partial();

export const UpdateNotificationSettingsSchema = z.object({
  pushEnabled: z.boolean().optional(),
  discordEnabled: z.boolean().optional(),
  /** Empty string removes the webhook. */
  discordWebhookUrl: z.string().max(300).optional(),
  kinds: z.record(z.enum(NOTIFICATION_KINDS), KindSetting).optional(),
});
export type UpdateNotificationSettings = z.infer<typeof UpdateNotificationSettingsSchema>;

/**
 * Web Push endpoints are fetched BY THE SERVER, so a client-chosen URL is an SSRF primitive (internal services,
 * cloud metadata, port scans through delivery errors). Only https URLs on the push services the browsers use
 * (FCM, Mozilla autopush, Apple, Windows/Edge) are accepted; credentials, ports and IP literals are refused.
 */
const PUSH_HOST_SUFFIXES = ['fcm.googleapis.com', 'android.googleapis.com', 'push.services.mozilla.com', 'push.apple.com', 'notify.windows.com'];
export function isAllowedPushEndpoint(raw: string): boolean {
  let u: URL;
  try { u = new URL(raw); } catch { return false; }
  if (u.protocol !== 'https:' || u.username || u.password || u.port) return false;
  const host = u.hostname.toLowerCase();
  if (host.startsWith('[') || /^[0-9.]+$/.test(host)) return false;
  return PUSH_HOST_SUFFIXES.some(s => host === s || host.endsWith('.' + s));
}

export const PushSubscriptionSchema = z.object({
  endpoint: z.string().url().max(1000).refine(isAllowedPushEndpoint, 'Ismeretlen push-szolgáltató.'),
  keys: z.object({ p256dh: z.string().min(10).max(200), auth: z.string().min(8).max(100) }),
});
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionSchema>;

/** Only Discord's own webhook hosts are accepted (no server-side requests to arbitrary addresses). */
export const DISCORD_WEBHOOK_RE = /^https:\/\/(?:(?:ptb|canary)\.)?(?:discord|discordapp)\.com\/api\/webhooks\/\d{5,25}\/[\w-]{20,200}$/;

// ---- Seasons ----

export interface SeasonView {
  id: string;
  number: number;
  name: string;
  state: 'upcoming' | 'active' | 'ended';
  startsAt: string;
  endsAt: string | null;
  endedAt: string | null;
}

/** Points per achievement counted inside the season window; assets carried over do not score. */
export const SEASON_SCORE_WEIGHTS = {
  systems: 20,   // systems acquired during the season
  techs: 15,     // technologies completed during the season
  battles: 25,   // battles won during the season
  stations: 10,  // stations built during the season
  events: 10,    // galactic-event decisions taken (not defaulted)
} as const;
export type ScoreStat = keyof typeof SEASON_SCORE_WEIGHTS;
export type SeasonStats = Record<ScoreStat, number>;

export function seasonScore(stats: SeasonStats): number {
  let total = 0;
  for (const k of Object.keys(SEASON_SCORE_WEIGHTS) as ScoreStat[]) total += (stats[k] ?? 0) * SEASON_SCORE_WEIGHTS[k];
  return total;
}

/**
 * Soft reset rules (docs/seasons.md). Stockpiles above `keepFloor × starting amount` shrink to `carryOver`
 * of the excess; everything else (systems, stations, ships, technology, treaties, alliances) is kept.
 */
export const SEASON_RESET_RULES = {
  floorMultiplier: 2,
  carryOver: 0.25,
} as const;

export function softResetAmount(current: number, starting: number): number {
  const floor = starting * SEASON_RESET_RULES.floorMultiplier;
  if (current <= floor) return current;
  return floor + Math.floor((current - floor) * SEASON_RESET_RULES.carryOver);
}

export interface RankingRow {
  rank: number;
  name: string;
  score: number;
  stats?: SeasonStats;
  /** Equipped title (a kept cosmetic), players only. */
  title?: string | null;
  allianceTag?: string | null;
  members?: number;
  isYou?: boolean;
}

export interface RankingsView {
  season: SeasonView | null;
  players: RankingRow[];
  alliances: RankingRow[];
  you: { rank: number; score: number } | null;
}

export interface HallOfFameEntry {
  seasonNumber: number;
  seasonName: string;
  kind: 'player' | 'alliance';
  rank: number;
  name: string;
  score: number;
}

export interface CosmeticView { key: string; kind: 'title' | 'badge'; label: string; equipped: boolean; seasonNumber: number | null }

export const StartSeasonSchema = z.object({
  name: z.string().trim().min(2).max(60).optional(),
  endsAt: z.string().datetime().optional(),
  reason: z.string().trim().min(3).max(300),
});
export const EndSeasonSchema = z.object({
  startNext: z.boolean().optional(),
  nextName: z.string().trim().min(2).max(60).optional(),
  nextEndsAt: z.string().datetime().optional(),
  reason: z.string().trim().min(3).max(300),
});
export const EquipCosmeticSchema = z.object({ key: z.string().min(1).max(80).nullable() });

// ---- Alliances ----

export const ALLIANCE_GOAL_KINDS = ['systems', 'techs', 'battles', 'stations'] as const;
export type AllianceGoalKind = (typeof ALLIANCE_GOAL_KINDS)[number];
export const ALLIANCE_GOAL_LABELS: Record<AllianceGoalKind, string> = {
  systems: 'Rendszer megszerzése', techs: 'Technológia kikutatása', battles: 'Megnyert csata', stations: 'Állomás építése',
};
export const ALLIANCE_MAX_MEMBERS = 12;

export const CreateAllianceSchema = z.object({
  name: z.string().trim().min(3).max(32),
  tag: z.string().trim().regex(/^[A-Za-z0-9]{2,5}$/, 'A rövidítés 2–5 betű vagy szám.'),
});
export const AllianceInviteSchema = z.object({ targetCivId: z.string().uuid() });
export const CreateAllianceGoalSchema = z.object({
  kind: z.enum(ALLIANCE_GOAL_KINDS),
  target: z.number().int().min(1).max(500),
});
export const AllianceWebhookSchema = z.object({
  discordWebhookUrl: z.string().max(300),
  discordEnabled: z.boolean().optional(),
});

export interface AllianceGoalView {
  id: string;
  kind: AllianceGoalKind;
  label: string;
  target: number;
  progress: number;
  status: 'active' | 'completed' | 'expired';
  createdAt: string;
  completedAt: string | null;
}

export interface AllianceMemberView { civId: string; name: string; role: 'leader' | 'officer' | 'member'; score: number }

export interface AllianceView {
  id: string;
  name: string;
  tag: string;
  role: 'leader' | 'officer' | 'member';
  members: AllianceMemberView[];
  goals: AllianceGoalView[];
  discordConfigured: boolean;
  discordEnabled: boolean;
  invites: Array<{ id: string; targetCivId: string; targetName: string }>;
}

export interface AllianceInviteView { id: string; allianceName: string; allianceTag: string; invitedBy: string }

export interface AllianceOverview {
  alliance: AllianceView | null;
  incomingInvites: AllianceInviteView[];
}

// ---- Galactic events ----

export const GALACTIC_EVENT_KINDS = ['supernova', 'alien_signal', 'crisis'] as const;
export type GalacticEventKind = (typeof GALACTIC_EVENT_KINDS)[number];

export interface EventEffect { metal: number; energy: number; credits: number }
export interface EventOptionDef { key: string; label: string; description: string; effect: EventEffect }
export interface EventKindDef {
  title: string;
  /** Text shown to everyone; `{system}` is replaced by the system name only when the player knows it. */
  intro: string;
  options: EventOptionDef[];
  /** Applied when the player did not decide before the deadline. */
  defaultOption: string;
}

const fx = (metal: number, energy: number, credits: number): EventEffect => ({ metal, energy, credits });

export const GALACTIC_EVENT_DEFS: Record<GalacticEventKind, EventKindDef> = {
  supernova: {
    title: 'Szupernova',
    intro: 'Egy csillag a birodalmad rendszerei között szupernovává robban: a sugárzás hullámokban éri el a környéket.',
    defaultOption: 'ride_out',
    options: [
      { key: 'evacuate', label: 'Kitelepítés', description: 'Az állomások személyzetét kimenekíted: kreditbe kerül, de nincs veszteség.', effect: fx(0, 0, -150) },
      { key: 'ride_out', label: 'Kivárás', description: 'A rendszer kibírja, de a sugárzás károsítja a berendezéseket.', effect: fx(-120, 40, 0) },
      { key: 'harvest', label: 'Energiabegyűjtés', description: 'A kitörés energiáját befogod; kockázatos, fémbe kerül.', effect: fx(-200, 260, 0) },
    ],
  },
  alien_signal: {
    title: 'Idegen jel',
    intro: 'Ismeretlen eredetű jel érkezik a galaxis mélyéről{system}. Mindenki dönthet, mit kezd vele.',
    defaultOption: 'ignore',
    options: [
      { key: 'investigate', label: 'Kivizsgálás', description: 'Kutatócsapatot küldesz: energiába kerül, de értékes leletet hozhat.', effect: fx(180, -80, 120) },
      { key: 'decode', label: 'Megfejtés', description: 'Hosszú elemzés: kevés haszon, de biztonságos.', effect: fx(40, -20, 60) },
      { key: 'ignore', label: 'Figyelmen kívül hagyás', description: 'Nem történik semmi.', effect: fx(0, 0, 0) },
    ],
  },
  crisis: {
    title: 'Energiaválság',
    intro: 'A galaxis energiapiaca összeomlik; mindenki készletei megrendülnek.',
    defaultOption: 'ration',
    options: [
      { key: 'stockpile', label: 'Felvásárlás', description: 'Kreditből energiát vásárolsz a válság árán.', effect: fx(0, 150, -200) },
      { key: 'ration', label: 'Takarékosság', description: 'Visszafogod a fogyasztást: kis veszteség.', effect: fx(-40, -40, 0) },
      { key: 'speculate', label: 'Spekuláció', description: 'Fémet adsz el a válságban, kreditért.', effect: fx(-160, 0, 180) },
    ],
  },
};

export interface GalacticEventView {
  id: string;
  kind: GalacticEventKind;
  title: string;
  intro: string;
  status: 'active' | 'resolved';
  startsAt: string;
  decisionEndsAt: string;
  /** Present only when the player already knows the system. */
  systemName: string | null;
  options: Array<{ key: string; label: string; description: string; effect: EventEffect }>;
  /** The player's decision (or the applied default after resolution). */
  chosen: string | null;
  /** The applied effect, after resolution. */
  applied: EventEffect | null;
  canDecide: boolean;
}

export const GalacticChoiceSchema = z.object({ optionKey: z.string().min(1).max(40) });
